import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import ProductCard from '../components/ProductCard';
import Toast, { useToast } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import './FeedPage.css';

function MultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };
  const displayText = selected.length === 0 ? label : `${label} (${selected.length})`;

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleOpen = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 2, left: rect.left });
    }
    setOpen(!open);
  };

  return (
    <div className="multi-select" ref={ref}>
      <button className="input multi-select-btn" ref={btnRef} onClick={handleOpen}>
        {displayText} <span className="multi-chevron">{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && createPortal(
        <div className="multi-dropdown" ref={ref} style={{ top: pos.top, left: pos.left }}>
          {options.map(opt => (
            <label key={opt} className="multi-option">
              <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
              <span>{opt.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}</span>
            </label>
          ))}
          {options.length === 0 && <div className="multi-empty">No options</div>}
        </div>,
        document.body
      )}
    </div>
  );
}

export default function FeedPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const [filters, setFilters] = useState(() => {
    try { const saved = sessionStorage.getItem('feed-filters'); return saved ? JSON.parse(saved) : { brand: [], color: [], grade: [], product_type: [], search: '', priceMin: '', priceMax: '' }; }
    catch { return { brand: [], color: [], grade: [], product_type: [], search: '', priceMin: '', priceMax: '' }; }
  });
  const [hideTarget, setHideTarget] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [assignUserId, setAssignUserId] = useState('');
  const [pillarsOpen, setPillarsOpen] = useState(false);
  const [sortBy, setSortBy] = useState(() => sessionStorage.getItem('feed-sortBy') || 'auction_end_asc');
  const [pendingBid, setPendingBid] = useState(null);
  const loadMoreRef = useRef(null);
  const scrollRestoredRef = useRef(false);

  useEffect(() => { sessionStorage.setItem('feed-filters', JSON.stringify(filters)); }, [filters]);
  useEffect(() => { sessionStorage.setItem('feed-sortBy', sortBy); }, [sortBy]);

  const { data: filterOptions } = useQuery({
    queryKey: ['filter-options'],
    queryFn: () => api.get('/products/filters/options').then(r => r.data),
  });

  const LIMIT = 24;

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['products', filters, sortBy],
    queryFn: ({ pageParam = 1 }) => {
      const params = new URLSearchParams();
      params.append('page', pageParam);
      params.append('limit', LIMIT);
      if (filters.search) params.append('search', filters.search);
      filters.brand.forEach(b => params.append('brand', b));
      filters.color.forEach(c => params.append('color', c));
      filters.grade.forEach(g => params.append('grade', g));
      filters.product_type.forEach(t => params.append('product_type', t));
      if (filters.priceMin) params.append('price_min', filters.priceMin);
      if (filters.priceMax) params.append('price_max', filters.priceMax);
      if (sortBy) params.append('sort', sortBy);
      return api.get(`/products?${params.toString()}`).then(r => r.data);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.products.length, 0);
      if (loaded < lastPage.total) return allPages.length + 1;
      return undefined;
    },
  });

  const allProducts = data?.pages?.flatMap(p => p.products) ?? [];
  const total = data?.pages?.[0]?.total ?? 0;
  const userType = data?.pages?.[0]?.user_type ?? 'individual';

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Save scroll position and page count before navigating to a product
  useEffect(() => {
    const handleClick = (e) => {
      const card = e.target.closest('.product-card');
      if (card) {
        sessionStorage.setItem('feed-scroll-pos', String(window.scrollY));
        const pageCount = data?.pages?.length || 1;
        sessionStorage.setItem('feed-page-count', String(pageCount));
      }
    };
    const grid = document.querySelector('.product-grid');
    if (grid) grid.addEventListener('click', handleClick);
    return () => { if (grid) grid.removeEventListener('click', handleClick); };
  });

  // On mount, pre-fetch pages that were loaded before navigation
  const prefetchingRef = useRef(false);
  useEffect(() => {
    const savedPages = sessionStorage.getItem('feed-page-count');
    const savedPos = sessionStorage.getItem('feed-scroll-pos');
    if (!savedPages || !savedPos || prefetchingRef.current) return;
    const targetPageCount = parseInt(savedPages, 10);
    const currentPageCount = data?.pages?.length || 0;
    if (currentPageCount > 0 && currentPageCount < targetPageCount && hasNextPage && !isFetchingNextPage) {
      prefetchingRef.current = true;
      fetchNextPage();
      prefetchingRef.current = false;
    }
  }, [data?.pages?.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Restore scroll position once all saved pages are loaded
  useEffect(() => {
    if (scrollRestoredRef.current) return;
    const savedPos = sessionStorage.getItem('feed-scroll-pos');
    const savedPages = sessionStorage.getItem('feed-page-count');
    if (!savedPos) return;
    const targetPageCount = savedPages ? parseInt(savedPages, 10) : 1;
    const currentPageCount = data?.pages?.length || 0;
    if (currentPageCount >= targetPageCount && allProducts.length > 0) {
      scrollRestoredRef.current = true;
      const pos = parseInt(savedPos, 10);
      sessionStorage.removeItem('feed-scroll-pos');
      sessionStorage.removeItem('feed-page-count');
      requestAnimationFrame(() => {
        window.scrollTo(0, pos);
      });
    }
  }, [allProducts.length, data?.pages?.length]);

  const { data: wishlistIds = [] } = useQuery({
    queryKey: ['wishlist-ids'],
    queryFn: () => api.get('/wishlist/ids').then(r => r.data),
  });

  const toggleWishlist = useMutation({
    mutationFn: (productId) => {
      if (wishlistIds.includes(productId)) return api.delete(`/wishlist/${productId}`);
      return api.post('/wishlist', { product_id: productId });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wishlist-ids'] }),
  });

  const quickBid = useMutation({
    mutationFn: ({ productId, amount }) => api.post('/bids', { product_id: productId, amount_euro: amount }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['products'] }); showToast('Your bid has been placed successfully.'); },
    onError: (err) => { showToast(err.response?.data?.detail || 'Your bid is too low. Please increase your amount.', 'error'); },
  });

  const hideProduct = useMutation({
    mutationFn: (productId) => api.post(`/admin/products/${productId}/hide`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['filter-options'] });
      setHideTarget(null);
    },
  });

  const { data: adminUsers = [] } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get('/admin/users').then(r => r.data),
    enabled: !!user?.is_admin,
  });

  const assignWishlist = useMutation({
    mutationFn: () => api.post('/admin/wishlist/assign', {
      user_id: assignUserId,
      product_ids: selectedProductIds,
    }),
    onSuccess: (res) => {
      showToast(res.data?.message || 'Products assigned');
      setSelectedProductIds([]);
      setAssignUserId('');
    },
    onError: (err) => alert(err.response?.data?.detail || 'Assign failed'),
  });

  const toggleSelect = (productId) => {
    setSelectedProductIds(ids =>
      ids.includes(productId) ? ids.filter(i => i !== productId) : [...ids, productId]
    );
  };

  const setFilter = (key, val) => {
    setFilters(f => ({ ...f, [key]: val }));
    sessionStorage.removeItem('feed-scroll-pos');
    sessionStorage.removeItem('feed-page-count');
    scrollRestoredRef.current = false;
  };

  const hasFilters = filters.brand.length || filters.color.length || filters.grade.length || filters.product_type.length || filters.search || filters.priceMin || filters.priceMax;

  return (
    <div className="feed-page">
      <h2 className="hero-tagline">Japan's finest luxury auctions, delivered to you</h2>
      <button className="service-toggle" onClick={() => setPillarsOpen(!pillarsOpen)}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`service-arrow ${pillarsOpen ? 'open' : ''}`}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {pillarsOpen && (
        <div className="service-banner">
          <div className="service-pillar">
            <span className="service-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="22" x2="5" y2="4" /><line x1="19" y1="22" x2="19" y2="4" /><path d="M2 4 h20" /><path d="M3 8 h18" /><path d="M1 2.5 C1 2.5 6 0.5 12 0.5 C18 0.5 23 2.5 23 2.5" />
              </svg>
            </span>
            <div className="service-text">
              <div className="service-title">From Japan with love</div>
              <div className="service-subtitle">Curated from Japan's top auction houses</div>
            </div>
          </div>
          <div className="service-divider" />
          <div className="service-pillar">
            <span className="service-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
            </span>
            <div className="service-text">
              <div className="service-title">Authenticity guaranteed</div>
              <div className="service-subtitle">Verified before delivery, certificate included</div>
            </div>
          </div>
          <div className="service-divider" />
          <div className="service-pillar">
            <span className="service-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
            </span>
            <div className="service-text">
              <div className="service-title">All-inclusive pricing</div>
              <div className="service-subtitle">Shipping, customs, duties and auth included</div>
            </div>
          </div>
        </div>
      )}
      <div className="feed-header">
        <h1>Current Auction</h1>
        {total > 0 && <span className="feed-count">{total} items</span>}
      </div>

      {user?.is_admin && selectedProductIds.length > 0 && (
        <div className="admin-assign-bar">
          <span className="admin-assign-count">{selectedProductIds.length} selected</span>
          <select
            className="input"
            value={assignUserId}
            onChange={e => setAssignUserId(e.target.value)}
          >
            <option value="">Assign to user…</option>
            {adminUsers
              .filter(u => u.statut === 'approved' && !u.is_admin)
              .map(u => (
                <option key={u.id} value={u.id}>{u.prenom} {u.nom} ({u.email})</option>
              ))}
          </select>
          <button
            className="btn btn-primary btn-sm"
            disabled={!assignUserId || assignWishlist.isPending}
            onClick={() => assignWishlist.mutate()}
          >
            {assignWishlist.isPending ? 'Assigning…' : 'Assign'}
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => setSelectedProductIds([])}>
            Clear
          </button>
        </div>
      )}

      <div className="feed-controls">
        <input
          className="input search-input"
          placeholder="Search brand, model..."
          value={filters.search}
          onChange={e => setFilter('search', e.target.value)}
        />
      </div>
      <div className="filters-sticky">
          <div className="filters">
            <MultiSelect label="All brands" options={filterOptions?.brands || []} selected={filters.brand} onChange={v => setFilter('brand', v)} />
            <MultiSelect label="All colors" options={filterOptions?.colors || []} selected={filters.color} onChange={v => setFilter('color', v)} />
            <MultiSelect label="All grades" options={['S', 'A', 'AB', 'B', 'BC', 'C', 'D']} selected={filters.grade} onChange={v => setFilter('grade', v)} />
            <MultiSelect label="All categories" options={filterOptions?.types || []} selected={filters.product_type} onChange={v => setFilter('product_type', v)} />
            <div className="price-filter">
              <span className="price-filter-prefix">&euro;</span>
              <input
                className="input price-filter-input"
                type="number"
                min="0"
                placeholder="Min"
                value={filters.priceMin}
                onChange={e => setFilter('priceMin', e.target.value)}
              />
              <span className="price-filter-sep">—</span>
              <span className="price-filter-prefix">&euro;</span>
              <input
                className="input price-filter-input"
                type="number"
                min="0"
                placeholder="Max"
                value={filters.priceMax}
                onChange={e => setFilter('priceMax', e.target.value)}
              />
            </div>
            {hasFilters && (
              <button className="btn btn-outline btn-sm" onClick={() => { setFilters({ brand: [], color: [], grade: [], product_type: [], search: '', priceMin: '', priceMax: '' }); sessionStorage.removeItem('feed-scroll-pos'); sessionStorage.removeItem('feed-page-count'); scrollRestoredRef.current = false; }}>
                Clear
              </button>
            )}
          </div>
          {(() => {
            const brands = (filterOptions?.brands || []).map(b => b.toUpperCase());
            const colors = (filterOptions?.colors || []).map(c => c.toLowerCase());
            const collections = [
              { label: 'Chanel Black', show: brands.includes('CHANEL') && colors.includes('black'), active: filters.brand.includes('CHANEL') && filters.color.includes('black'), onClick: () => setFilters({ brand: ['CHANEL'], color: ['black'], grade: [], product_type: [], search: '', priceMin: '', priceMax: '' }) },
              { label: 'Chanel Colors', show: brands.includes('CHANEL') && colors.some(c => c !== 'black'), active: filters.brand.includes('CHANEL') && filters.color.length > 0 && !filters.color.includes('black'), onClick: () => setFilters({ brand: ['CHANEL'], color: ['red', 'pink', 'blue', 'green', 'beige', 'white', 'brown', 'orange', 'purple', 'gold', 'silver', 'multicolor'], grade: [], product_type: [], search: '', priceMin: '', priceMax: '' }) },
              { label: 'Under €600', show: true, active: filters.priceMax === '600' && !filters.brand.length, onClick: () => setFilters({ brand: [], color: [], grade: [], product_type: [], search: '', priceMax: '600', priceMin: '' }) },
              { label: 'Fendi Baguette', show: brands.includes('FENDI'), active: filters.brand.includes('FENDI') && filters.search === 'baguette', onClick: () => setFilters({ brand: ['FENDI'], color: [], grade: [], product_type: [], search: 'baguette', priceMin: '', priceMax: '' }) },
            ].filter(c => c.show);
            return collections.length > 0 ? (
              <div className="feed-collections">
                {collections.map(c => (
                  <button key={c.label} className={`collection-btn ${c.active ? 'active' : ''}`} onClick={c.onClick}>{c.label}</button>
                ))}
              </div>
            ) : null;
          })()}
          <div className="feed-sort">
            <label className="sort-label">Sort by</label>
            <select className="input sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="auction_end_asc">Ending soonest</option>
              <option value="auction_end_desc">Ending latest</option>
              <option value="price_asc">Price: low to high</option>
              <option value="price_desc">Price: high to low</option>
            </select>
          </div>
      </div>

      {isLoading ? (
        <div className="feed-loading"><div className="spinner" /></div>
      ) : (
        <>
          <div className="product-grid">
            {allProducts.map(p => (
              <ProductCard
                key={p.id}
                product={p}
                isWishlisted={wishlistIds.includes(p.id)}
                onToggleWishlist={id => toggleWishlist.mutate(id)}
                onQuickBid={(id, amount) => setPendingBid({ productId: id, amount })}
                onBidError={(msg) => showToast(msg, 'error')}
                isAdmin={user?.is_admin}
                onHide={id => setHideTarget(id)}
                selectable={user?.is_admin}
                selected={selectedProductIds.includes(p.id)}
                onToggleSelect={toggleSelect}
                userType={userType}
              />
            ))}
          </div>
          {allProducts.length === 0 && !isFetchingNextPage && (
            <div className="feed-empty">No products match your filters.</div>
          )}
          <div ref={loadMoreRef} className="load-more-sentinel" />
          {isFetchingNextPage && (
            <div className="feed-loading-more"><div className="spinner spinner-sm" /></div>
          )}
        </>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      <ConfirmDialog
        open={!!hideTarget}
        title="Hide this item?"
        message="Sure you want to hide this item? It will no longer appear in the feed until you unhide it."
        confirmLabel="Hide"
        onConfirm={() => hideTarget && hideProduct.mutate(hideTarget)}
        onCancel={() => setHideTarget(null)}
      />
      <ConfirmDialog
        open={!!pendingBid}
        title="Confirm your bid"
        message={`By placing this bid of €${pendingBid?.amount || 0}, you commit to purchasing this item if your bid wins. This action is final and cannot be cancelled.`}
        confirmLabel="Confirm bid"
        onConfirm={() => { if (pendingBid) quickBid.mutate(pendingBid); setPendingBid(null); }}
        onCancel={() => setPendingBid(null)}
      />
    </div>
  );
}
